import { useCallback, useEffect, useMemo, useState } from 'react'
import { type Location, unstable_useBlocker } from 'react-router-dom'

import { Dialog } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

const useWorkflowNavConfirmModal = () => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const [navigationBlocked, setNavigationBlocked] = useState(false) //exposed to consumers for control

  // Browser navigation to warn user before leaving page (browser navigation like refresh/close)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationBlocked) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [navigationBlocked])

  // Custom navigation prevention
  // Block in-app navigation (React Router)
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
      return (
        navigationBlocked &&
        (currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search)
      )
    },
    [navigationBlocked]
  )
  const blocker = unstable_useBlocker(shouldBlock)

  // Show modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setConfirmModalOpen(true)
      setPendingNavigation(() => () => blocker.proceed())
    }
  }, [blocker])

  // Save and proceed with navigation
  const onContinue = useCallback(async () => {
    setConfirmModalOpen(false)
    setPendingNavigation(null)
  }, [])

  // Discard changes and proceed with navigation
  const onDiscard = useCallback(() => {
    setNavigationBlocked(false)
    setConfirmModalOpen(false)
    pendingNavigation?.()
    setPendingNavigation(null)
  }, [pendingNavigation])

  // Cancel navigation and stay on current page
  const onCancel = useCallback(() => {
    setConfirmModalOpen(false)
    setPendingNavigation(null)
    // Reset the blocker to cancel navigation
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  // Memoize the component to prevent unnecessary re-creation
  const NavConfirmModalWrapper = useMemo(() => {
    const Component = () => (
      <WorkflowNavConfirmModal
        open={confirmModalOpen}
        onClose={onCancel}
        onSaveChanges={onContinue}
        onDiscard={onDiscard}
      />
    )
    Component.displayName = 'WorkflowNavConfirmModalWrapper'
    return Component
  }, [confirmModalOpen, onCancel, onContinue, onDiscard])

  return {
    NavConfirmModal: NavConfirmModalWrapper,
    setNavigationBlocked,
  }
}

export type WorkflowNavConfirmModalProps = {
  open: boolean
  onClose?: () => void
  onSaveChanges?: () => void
  onDiscard?: () => void
}

export const WorkflowNavConfirmModal = ({
  open,
  onClose,
  onSaveChanges,
  onDiscard,
}: WorkflowNavConfirmModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      header={{
        type: 'warning',
        title: i18n.t('workflows.nav_confirm_modal.title'),
        description: i18n.t('workflows.nav_confirm_modal.description'),
      }}
      actions={{
        primary: {
          label: i18n.t('workflows.nav_confirm_modal.continue_editing'),
          onClick: onSaveChanges,
        },
        secondary: {
          label: i18n.t('workflows.nav_confirm_modal.discard'),
          onClick: onDiscard,
        },
      }}
    />
  )
}
